import { useEffect, useState } from 'react'
import {
  BenefitEligibilityTemplate,
  ClubBenefitEligibilitySelection,
  ClubBenefitOffer,
  useBenefitEligibilityTemplates,
  useClubBenefitEligibility,
  useSetClubBenefitEligibility,
} from '@/services/clubBenefits'
import { serverErrorMessage } from '@/services/httpService'

interface BenefitEligibilityPickerProps {
  clubId: number
  offers: ClubBenefitOffer[]
}

const NO_RULE = ''

/**
 * Who this club will accept a benefit from.
 *
 * A picker rather than an expression editor on purpose: directors are not rule authors, and the
 * canned templates are also what makes these predicates pushable into the search query. What gets
 * stored is an ordinary club-scoped targeting rule either way, so the full editor can replace this
 * later without a migration.
 *
 * Only shown to clubs whitelisted to self-manage; for everyone else DUPR sets the rules.
 */
export const BenefitEligibilityPicker = ({ clubId, offers }: BenefitEligibilityPickerProps) => {
  const { data: catalog } = useBenefitEligibilityTemplates(clubId)
  const { data: selections } = useClubBenefitEligibility(clubId)
  const save = useSetClubBenefitEligibility(clubId)

  const templates = catalog?.templates ?? []
  // `canEdit`, not `selfManage`: the club being whitelisted is only half of it, and the caller
  // also needs `benefits.rules:edit` — which a plain director does not have. Gating on the club
  // alone showed directors a picker whose every save came back 401.
  if (!catalog?.canEdit || templates.length === 0) return null

  return (
    <div className="mt-6 border-t border-gray-100 pt-4">
      <h3 className="text-sm font-medium text-gray-900">Who can claim at your club</h3>
      <p className="mt-0.5 text-xs text-gray-500">
        Leave this open to accept from any member holding the benefit, or narrow it to a group.
      </p>

      {offers.length === 0 ? (
        // There is one rule per offered benefit, so with nothing offered there is genuinely nothing
        // to configure — but rendering nothing at all made that look like a broken screen, and a
        // club with rules permission and no offers had no way to tell which of the two it was in.
        <p className="mt-3 text-xs text-gray-500">
          Switch on a benefit above first — rules are set per benefit, so there is nothing to narrow until your club
          offers one.
        </p>
      ) : (
        <div className="mt-3 space-y-3">
          {offers.map((offer) => (
            <BenefitRow
              key={offer.id}
              bundleUrn={offer.bundleUrn}
              benefitLabel={offer.label}
              templates={templates}
              // `?? null`, never left undefined: "this club has chosen no rule for this benefit" is
              // a real, meaningful state — it means anyone holding the benefit may claim — and it
              // is stored as the absence of a rule rather than as an "everyone" template.
              selection={selections?.find((s) => s.bundleUrn === offer.bundleUrn) ?? null}
              saving={save.isLoading}
              onSave={save.mutate}
            />
          ))}
        </div>
      )}

      {/* The server's own sentence where it has one — it is the sentence that says what to do
          ("This club's benefits are managed by DUPR"), where the generic fallback only invites the
          same failing save again. */}
      {save.isError && (
        <p className="mt-2 text-xs text-red-600">
          {serverErrorMessage(save.error, 'Could not save that rule. Please try again.')}
        </p>
      )}
    </div>
  )
}

interface BenefitRowProps {
  bundleUrn: string
  benefitLabel: string
  templates: BenefitEligibilityTemplate[]
  selection: ClubBenefitEligibilitySelection | null
  saving: boolean
  onSave: (selection: ClubBenefitEligibilitySelection | { bundleUrn: string; templateKey: null }) => void
}

const BenefitRow = ({ bundleUrn, benefitLabel, templates, selection, saving, onSave }: BenefitRowProps) => {
  const [templateKey, setTemplateKey] = useState(selection?.templateKey ?? NO_RULE)
  const [params, setParams] = useState<Record<string, string>>(selection?.parameters ?? {})

  // The server is the source of truth: a save, or another director's change arriving on refetch,
  // has to win over what is sitting in local state.
  useEffect(() => {
    setTemplateKey(selection?.templateKey ?? NO_RULE)
    setParams(selection?.parameters ?? {})
  }, [selection?.templateKey, selection?.parameters])

  const template = templates.find((t) => t.key === templateKey)
  const missingRequired = (template?.parameters ?? []).some((p) => p.required && !params[p.key]?.trim())

  return (
    <div className="rounded border border-gray-200 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-gray-800">{benefitLabel}</span>
        <select
          className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700"
          value={templateKey}
          onChange={(e) => {
            setTemplateKey(e.target.value)
            setParams({})
          }}
        >
          <option value={NO_RULE}>Anyone with this benefit</option>
          {templates.map((t) => (
            <option key={t.key} value={t.key}>
              {t.label}
            </option>
          ))}
        </select>

        {template?.parameters.map((p) => (
          <label key={p.key} className="flex items-center gap-1 text-xs text-gray-600">
            {p.label}
            <input
              type="number"
              className="w-20 rounded border border-gray-300 px-2 py-1 text-xs"
              // `?? undefined` at the DOM boundary only: React drops an attribute for `undefined`
              // but renders `min="null"` for null, and the server sends null to mean "no bound".
              min={p.type.min ?? undefined}
              max={p.type.max ?? undefined}
              value={params[p.key] ?? ''}
              onChange={(e) => setParams((prev) => ({ ...prev, [p.key]: e.target.value }))}
            />
          </label>
        ))}

        <button
          className="rounded bg-gray-900 px-2.5 py-1 text-xs text-white transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={saving || missingRequired}
          onClick={() =>
            onSave(
              templateKey === NO_RULE
                ? { bundleUrn, templateKey: null }
                : { bundleUrn, templateKey, parameters: params }
            )
          }
        >
          Save
        </button>
      </div>

      {template && <p className="mt-1.5 text-xs text-gray-500">{template.description}</p>}
    </div>
  )
}
